import React, { useState } from 'react';

interface SerialInfoData {
    serialNumber: string;
}

interface Article {
    id: string;
    name: string;
    description?: string;
}

interface SerialInfoPopupProps {
    open: boolean;
    onClose: () => void;
    selectedArticle?: Article | null;
    onSave?: (data: SerialInfoData) => void;
}

const SerialInfoPopup: React.FC<SerialInfoPopupProps> = ({
    open,
    onClose,
    selectedArticle,
    onSave
}) => {
    const [formData, setFormData] = useState<SerialInfoData>({
        serialNumber: ''
    });

    const [errors, setErrors] = useState<Partial<SerialInfoData>>({});

    const handleInputChange = (field: keyof SerialInfoData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<SerialInfoData> = {};

        if (!formData.serialNumber.trim()) {
            newErrors.serialNumber = 'Seriennummer ist erforderlich';
        }
      
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            if(onSave){
                onSave(formData);
            }
            handleClose();
        }
    };

    const handleClose = () => {
        setFormData({
            serialNumber: ''
        });
        setErrors({});
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Seriennummer eingeben
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Selected Article Info */}
                    {selectedArticle && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm font-medium text-blue-800 mb-1">
                                Ausgewählter Artikel:
                            </div>
                            <div className="font-semibold text-gray-900">
                                {selectedArticle.name}
                            </div>
                            {selectedArticle.description && (
                                <div className="text-sm text-gray-600 mt-1">
                                    {selectedArticle.description}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Fields */}
                    <div className="space-y-4">
                        {/* Chargennummer */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Seriennummer *
                            </label>
                            <input
                                type="text"
                                value={formData.serialNumber}
                                onChange={handleInputChange('serialNumber')}
                                placeholder="z.B. 2024-001"
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.serialNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.serialNumber && (
                                <p className="mt-1 text-sm text-red-600">{errors.serialNumber}</p>
                            )}
                        </div>

                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Speichern
                    </button>
                </div>
            </div>
        </div>
    );
};


export default SerialInfoPopup;